/* eslint-disable indent */
import { Checkbox, FormControlLabel, Grid } from '@mui/material';
import { getIn } from 'formik';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo } from 'react';

import { DollarIcon } from 'assets/v2/icons';
import Calendar from 'components/v2/Calendar';
import InputField, { SIZES } from 'components/v2/InputField';
import { zoneIgnoredDate } from 'utils/helpers';
import useFormFieldValidator from 'utils/hooks/useFormFieldValidator';
import { getTimezoneLocalISO } from 'utils/v2';

/**
 * TripFlightsForm
 *
 * A reusable component for managing flights-related fields in trip forms
 *
 * Handles departure/return dates
 * number of attendants, flight cost, and advanced payment checkbox.
 *
 * @param {Object} props
 * @param {Object} props.formik - Formik instance
 * @param {string} props.fieldPrefix - Dot-notation path for flights, e.g. 'ancillaryServices.flights[0]' or 'itineraryForm.legs[0].flights'
 * @param {boolean} props.isLoading - Disables input fields while loading
 * @param {string} props.timezone - Timezone string for date conversion (e.g., 'America/Los_Angeles')
 * @param {Object} [props.schema] - Optional Yup schema for required field validation
 * @param {string} [props.flightAmountTitle='Cost of Flights Booked'] - Label for the flight cost input
 * @param {boolean} [props.isRequired=true] - Whether fields are required
 * @param {string} props.returnDateField - In the flight form data returned from the backend, the return flight information is usually in index 1.
 *                                         However, in some cases, the departure and return dates may be in the same index.
 */

const TripFlightsForm = ({
  formik,
  fieldPrefix,
  isLoading,
  timezone,
  schema,
  flightAmountTitle = 'Cost of Flights Booked',
  isRequired = true,
  returnDateField,
}) => {
  const { values, setFieldValue, setFieldTouched } = formik;

  const requiredFields = useMemo(
    () =>
      isRequired
        ? [
            `${fieldPrefix}.departureDateTime`,
            returnDateField,
            `${fieldPrefix}.numberOfAttendants`,
            `${fieldPrefix}.flightCostInCents`,
            'ancillaryServices.flights[1].flightCostInCents',
          ]
        : [],
    [fieldPrefix, isRequired, returnDateField],
  );

  const fieldGroups = useMemo(
    () => [
      [
        `${fieldPrefix}.departureDateTime`,
        returnDateField,
        `${fieldPrefix}.numberOfAttendants`,
        `${fieldPrefix}.advancedPaymentRequired`,
        `${fieldPrefix}.flightCostInCents`,
        'ancillaryServices.flights[1].flightCostInCents',
      ],
    ],
    [fieldPrefix, returnDateField],
  );

  const { fieldErrorMsg, fieldValidation, setUpdatedField } =
    useFormFieldValidator({
      formik,
      requiredFields,
      schema,
      fieldGroups,
    });

  const setValue = useCallback(
    async (fullPath, value) => {
      await setFieldValue(fullPath, value);
      setFieldTouched(fullPath);
      setUpdatedField(fullPath);
    },
    [setFieldValue, setFieldTouched, setUpdatedField],
  );

  const handleNumberChange = useCallback(
    (key) =>
      ({ target: { value } }) => {
        const newValue = value === '' ? value : parseInt(value, 10).toString();
        setValue(`${fieldPrefix}.${key}`, newValue);
      },
    [setValue],
  );

  const handleAmountChange = useCallback(
    ({ target: { value } }) => {
      if (value === '') {
        setValue(`${fieldPrefix}.flightCostInCents`, '');
        setValue('ancillaryServices.flights[1].flightCostInCents', '');
      } else {
        const roundedValue = Math.round(parseFloat(value) * 100);
        setValue(`${fieldPrefix}.flightCostInCents`, roundedValue.toString());
        setValue(
          'ancillaryServices.flights[1].flightCostInCents',
          roundedValue.toString(),
        );
      }
    },
    [setValue],
  );

  const handlePaymentChange = useCallback(
    ({ target: { checked } }) =>
      setValue(`${fieldPrefix}.advancedPaymentRequired`, checked),
    [setValue],
  );

  const handleDepartureChange = useCallback(
    (date) => {
      setValue(
        `${fieldPrefix}.departureDateTime`,
        date?.setZone?.(timezone, { keepLocalTime: true })?.toUTC()?.toISO() ||
          date.toString(),
      );
    },
    [setValue, timezone],
  );

  const handleReturnDateChange = useCallback(
    (date) => {
      setValue(
        returnDateField,
        date?.setZone?.(timezone, { keepLocalTime: true })?.toUTC()?.toISO() ||
          date.toString(),
      );
    },
    [setValue, returnDateField, timezone],
  );

  const departureDateRenderInput = useCallback(
    (params) => (
      <InputField
        {...params}
        required={isRequired}
        size={SIZES.MEDIUM}
        valid={fieldValidation[`${fieldPrefix}.departureDateTime`]}
        error={fieldErrorMsg[`${fieldPrefix}.departureDateTime`]}
      />
    ),
    [fieldErrorMsg, fieldValidation, fieldPrefix, isRequired],
  );

  const returnDateRenderInput = useCallback(
    (params) => (
      <InputField
        {...params}
        required={isRequired}
        size={SIZES.MEDIUM}
        valid={fieldValidation[returnDateField]}
        error={fieldErrorMsg[returnDateField]}
      />
    ),
    [fieldErrorMsg, fieldValidation, returnDateField, isRequired],
  );

  const numberOfAttendants = useMemo(() => {
    const formValue = getIn(values, `${fieldPrefix}.numberOfAttendants`);
    if (!formValue && formValue !== 0) return '';
    return formValue;
  }, [values, fieldPrefix]);

  const flightAmount = useMemo(() => {
    const formValue = getIn(values, `${fieldPrefix}.flightCostInCents`);
    if (formValue !== 0 && !formValue) return '';
    return formValue / 100;
  }, [values, fieldPrefix]);

  const departureDate = useMemo(() => {
    const formValue = getIn(values, `${fieldPrefix}.departureDateTime`);
    return getTimezoneLocalISO({ isoDate: formValue, timezone }) ?? formValue;
  }, [values, fieldPrefix, timezone]);

  const returnDate = useMemo(() => {
    const returnDateFieldFormValue = getIn(values, returnDateField);
    return (
      getTimezoneLocalISO({ isoDate: returnDateFieldFormValue, timezone }) ??
      returnDateFieldFormValue
    );
  }, [values, returnDateField, timezone]);

  const returnMinDate = useMemo(() => {
    const departureDateFormValue = getIn(
      values,
      `${fieldPrefix}.departureDateTime`,
    );
    return departureDateFormValue &&
      DateTime.fromISO(departureDateFormValue).isValid
      ? zoneIgnoredDate(departureDateFormValue)
      : undefined;
  }, [values, fieldPrefix]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <Calendar
          inputFormat="MM/dd/yyyy"
          label="Departure Date"
          value={departureDate}
          disabled={isLoading}
          onChange={handleDepartureChange}
          renderInput={departureDateRenderInput}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Calendar
          inputFormat="MM/dd/yyyy"
          label="Return Date"
          value={returnDate}
          disabled={isLoading}
          onChange={handleReturnDateChange}
          renderInput={returnDateRenderInput}
          minDate={returnMinDate}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <InputField
          type="number"
          autoComplete="off"
          label="Number of Attendants"
          value={numberOfAttendants}
          disabled={isLoading}
          required={isRequired}
          onChange={handleNumberChange('numberOfAttendants')}
          valid={fieldValidation[`${fieldPrefix}.numberOfAttendants`]}
          error={fieldErrorMsg[`${fieldPrefix}.numberOfAttendants`]}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <InputField
          type="number"
          autoComplete="off"
          adornment={<DollarIcon />}
          min={0}
          label={flightAmountTitle}
          disabled={isLoading}
          required={isRequired}
          value={flightAmount}
          onChange={handleAmountChange}
          valid={fieldValidation[`${fieldPrefix}.flightCostInCents`]}
          error={fieldErrorMsg[`${fieldPrefix}.flightCostInCents`]}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          checked={
            getIn(values, `${fieldPrefix}.advancedPaymentRequired`) || false
          }
          control={<Checkbox />}
          disabled={isLoading}
          label="Advanced"
          sx={{ mt: -1, ml: -1 }}
          onChange={handlePaymentChange}
        />
      </Grid>
    </Grid>
  );
};

TripFlightsForm.propTypes = {
  formik: PropTypes.object.isRequired,
  fieldPrefix: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  timezone: PropTypes.string,
  schema: PropTypes.object,
  flightAmountTitle: PropTypes.string,
  isRequired: PropTypes.bool,
  returnDateField: PropTypes.number,
};

export default memo(TripFlightsForm);
